import { get, post } from '../../lib/utils/axiosFetcher'; // dap.api
import * as axiosSiteApi from '../../lib/utils/axiosSiteApi';
import { logger } from '../../logger';
// import mockData, { smaGlobalSetting, mockProtocolRes } from './mockData';
import {
  IInitialFilterRes,
  IGetAvailableSitesParams,
  IGetAvailableSitesResponse,
  IProtocolListRes,
  EPR_TYPE,
  IAddSiteAuditTrailRow,
  IAuditChange,
  IAuditEntry,
  IAuditEventType,
  IAuditSource,
  IAuditStatus,
  IFetchSiteAuditTrailsRequest,
  ISiteAuditTrailRow,
} from './types';
import { ISiteMngData } from './types';
import axios, { AxiosResponse } from 'axios';

export enum Roles {
  AUTHOR = 'SMAAuthor',
  APPROVER = 'SMAApprover',
  ADMIN = 'Admin',
}

/** True when the value is a non-empty string that includes any whitespace (leading, trailing, or internal). */
export const siteIdentifierContainsWhitespace = (value: unknown): boolean => {
  if (typeof value !== 'string' || value === '') {
    return false;
  }
  return /\s/.test(value);
};

/** User-facing validation for Site ID (ctmsRowId) and Site Code before save. */
export const getSiteIdentifierWhitespaceValidationMessage = (
  ctmsRowId: unknown,
  siteCode: unknown
): string | null => {
  if (siteIdentifierContainsWhitespace(ctmsRowId)) {
    return 'Site ID cannot contain any spaces before or after the value. Remove all spaces and try again.';
  }
  if (siteIdentifierContainsWhitespace(siteCode)) {
    return 'Site Code cannot contain any spaces before or after the value. Remove all spaces and try again.';
  }
  return null;
};

export const formatSitesForTranslation = (
  sites: ISiteMngData[],
  toLang: string,
  fieldOptions: any
): any => {
  const getTranslateableFields = (fieldOptions: any) => {
    const translateableFields: string[] = [];
    for (const fields in fieldOptions) {
      if (fieldOptions[fields]?.isTranslatable) {
        // TODO change to field value for translation from isEditable
        translateableFields.push(fieldOptions[fields]?.fieldKey);
      }
    }
    return translateableFields;
  };

  const sitesDataRequest: any = [];
  sites.map((site: ISiteMngData) => {
    const translateableFields = getTranslateableFields(fieldOptions);
    const formattedObj: any = {};

    for (const [key, value] of Object.entries(site)) {
      if (translateableFields.includes(key)) {
        formattedObj[key] = value;
      }
    }
    formattedObj.id = site.uid;
    sitesDataRequest.push(formattedObj);
  });

  const formattedSites = {
    from: 'en', //TODO cross verify with deependra
    to: toLang,
    sitesDataRequest: sitesDataRequest,
  };

  return formattedSites;
};

function isBlankResponse(data: unknown): boolean {
  if (data == null) return true;
  if (typeof data === 'string') return data.trim() === '';
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

function extractErrorMessageFromResponse(data: unknown): string {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    const msg = o.message ?? o.Message ?? o.error ?? o.title ?? o.detail;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
    try {
      return JSON.stringify(data);
    } catch {
      return 'Request failed';
    }
  }
  return String(data);
}

export const fetchSites = async (
  protocolName: string,
  language: string,
  siteDetailTarget: string
): Promise<any[]> => {
  try {
    const reqBody = {
      protocolName,
      language,
      siteDetailTarget,
    };
    if (!reqBody.protocolName) {
      alert('Protocol is required');
    }
    if (!reqBody.language) {
      alert('Language is required');
    }
    // return mockData;
    const response = await post('/SMA/FetchSMASitesDataFromDb', reqBody);
    const responseData = response.data;
    if (Array.isArray(responseData) && responseData.length > 0) {
      return responseData;
    } else {
      return [];
    }
  } catch (_error) {
    return [];
  }
};

export interface ISaveSitesOptions {
  saveAndPublish?: boolean;
  /** When true, return API protocolItemId (publish / save-and-publish flows). */
  returnProtocolItemId?: boolean;
}

// Last staging-save error. saveSites writes it. FooterActions reads it into the audit Exception column.
let lastSaveSitesErrorMessage = '';

// Returns that error and clears it. Called from FooterActions after each save batch.
export function takeLastSaveSitesErrorMessage(): string {
  const message = lastSaveSitesErrorMessage;
  lastSaveSitesErrorMessage = '';
  return message;
}

export const saveSites = async (
  sites: ISiteMngData[],
  options?: ISaveSitesOptions
): Promise<string> => {
  try {
    const body = { sites, isSavePublish: options?.saveAndPublish };
    const response = await post('/SMA/SaveSitesDataInDbFromSma', body);
    const status = response.status;
    const data = response.data;
    if (status === 200 || status === 201) {
      if (options?.returnProtocolItemId || options?.saveAndPublish) {
        lastSaveSitesErrorMessage = '';
        return typeof data === 'string' ? data.trim() : '';
      }
      lastSaveSitesErrorMessage = '';
      return 'saved';
    }
    if (!isBlankResponse(data)) {
      logger.error('SaveSitesDataInDbFromSma failed', {
        status,
        errorMessage: extractErrorMessageFromResponse(data),
      });
    } else {
      logger.error('SaveSitesDataInDbFromSma failed', { status });
    }
    lastSaveSitesErrorMessage = extractErrorMessageFromResponse(data) || 'Save failed';
    return '';
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status != null && status !== 200 && status !== 201 && !isBlankResponse(data)) {
      logger.error('SaveSitesDataInDbFromSma failed', {
        status,
        errorMessage: extractErrorMessageFromResponse(data),
      });
    } else if (status != null && status !== 200 && status !== 201) {
      logger.error('SaveSitesDataInDbFromSma failed', { status });
    } else {
      logger.error('SaveSitesDataInDbFromSma request error', { error });
    }
    lastSaveSitesErrorMessage = extractErrorMessageFromResponse(data) || 'Save failed';
    return '';
  }
};

/** Matches DAP API `PublishMutationRequest` (PascalCase for .NET binding). */
export interface IPublishSiteDetailsRequest {
  EprId?: string | null;
  ProtocolItemId?: string | null;
  Language?: string | null;
}

/**
 * Triggers `/SMA/PublishSiteDetails` without awaiting or inspecting the response (fire-and-forget).
 */
export function publishSiteDetails(request: IPublishSiteDetailsRequest): void {
  const body = {
    EprId: request.EprId ?? null,
    ProtocolItemId: request.ProtocolItemId ?? null,
    Language: request.Language ?? null,
  };
  void post('/SMA/PublishSiteDetails', body).catch((error) => {
    logger.error('PublishSiteDetails async request failed', { error });
  });
}

export const translateSites = async (translationData: any): Promise<any> => {
  try {
    const reqBody = translationData;
    const response = await post('/SMA/TranslationSMASitesData', reqBody);
    const responseData = response.data;
    return responseData;
  } catch (_error) {
    return false;
  }
};

export const getSMAGlobalSetting = async (): Promise<IInitialFilterRes> => {
  try {
    // return smaGlobalSetting;
    const response = await axiosSiteApi.get('/SMA/GetSMAGlobalSetting');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const isSiteIdAlreadyAvailable = async (id: any): Promise<any> => {
  try {
    const response = await post(`/SMA/CheckExistingSitecode?siteCode=${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSMASiteSetting = async (eprId: string, lang: string): Promise<IProtocolListRes> => {
  try {
    const response = await axiosSiteApi.get(
      `/SMA/GetSMASiteSetting?eprId=${encodeURIComponent(eprId)}&language=${encodeURIComponent(
        lang
      )}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProtocolList = async (
  eprId: string,
  lang: string,
  isGlobal: boolean
): Promise<IProtocolListRes> => {
  try {
    // return mockProtocolRes;
    if (isGlobal) {
      const globalId = '{01442196-D652-4F46-997F-8A741C1D5FDC}';
      const response = await axiosSiteApi.get(
        `/SMA/GetGlobalProtocolItems?globalItemId=${globalId}&language=${lang}`
      );
      return { protocol: response.data };
    }
    return getSMASiteSetting(eprId, lang);
  } catch (error) {
    throw error;
  }
};

export interface IEprSiteContext {
  siteSettingsItemId: string;
  countryItemId: string;
  countryName: string;
  languageCode: string;
  countryCode: string;
  dataFolderItemId: string;
}

export const getEprSiteContext = async (
  eprId: string,
  language: string
): Promise<IEprSiteContext> => {
  try {
    const response = await axiosSiteApi.get(
      `/SMA/GetEprSiteContext?eprId=${encodeURIComponent(eprId)}&language=${encodeURIComponent(
        language
      )}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface IStateApiItem {
  stateItemID: string;
  itemName: string;
  itemPath: string;
  key: string;
  value: string;
  label: string;
  isTerritoryState: string;
  cc_TerritoryState: string;
}

export const getCountryStates = async (
  countryItemId: string,
  language: string
): Promise<IStateApiItem[]> => {
  try {
    const response = await axiosSiteApi.get(
      `/Sitecore/GetCountryStates?countryItemId=${encodeURIComponent(
        countryItemId
      )}&language=${encodeURIComponent(language)}`
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw error;
  }
};

const buildGetSitesQueryString = (params: Record<string, string>): string =>
  Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

export const validateAvailableSitesForm = (params: {
  epr: string;
  language: string;
  protocols: string[];
  stateItemId: string;
  siteContext: IEprSiteContext | null;
  locationMode: 'zipcode' | 'coordinates';
  zipcode: string;
  latitude: string;
  longitude: string;
}): string[] => {
  const errors: string[] = [];

  if (!params.epr) {
    errors.push('EPR is required.');
  }
  if (!params.language) {
    errors.push('Language is required.');
  }
  if (!params.protocols.length) {
    errors.push('At least one eligible protocol is required.');
  }
  if (!params.siteContext?.siteSettingsItemId || !params.siteContext?.dataFolderItemId) {
    errors.push('Site context is not loaded. Select EPR and language and try again.');
  }
  if (params.locationMode === 'zipcode') {
    if (!params.zipcode.trim()) {
      errors.push('ZIP Code is required.');
    }
  } else if (!params.latitude.trim() || !params.longitude.trim()) {
    errors.push('Latitude and longitude are required.');
  } else if (Number.isNaN(Number(params.latitude)) || Number.isNaN(Number(params.longitude))) {
    errors.push('Latitude and longitude must be valid numbers.');
  }

  return errors;
};

export const getAvailableSites = async (
  params: IGetAvailableSitesParams
): Promise<IGetAvailableSitesResponse> => {
  const queryParams: Record<string, string> = {
    EligibleProtocols: `${params.eligibleProtocols.join(',')},`,
    SiteRootPath: params.siteRootPath,
    Language: params.language,
    InvitedProtocol: '',
    InvitedSite: '',
    Latitude: params.latitude ?? '',
    Longitude: params.longitude ?? '',
    PatientAddress: '',
    PatientCity: '',
    ...(params.patientState ? { PatientState: params.patientState } : {}),
    ZipCode: params.zipCode ?? '',
    SiteSettingsId: params.siteSettingsId,
    SiteCount: String(params.siteCount ?? 5),
    isSitesRequired: 'true',
    EthnicityValue: '',
    protocolSettingsId: params.protocolSettingsId,
  };

  const response = await get(`/SiteSelection/GetSites?${buildGetSitesQueryString(queryParams)}`);
  return response.data;
};

/**
 * Adds uid to every site both parent and subRows
 *  uid is just a combination of smaRowId + _ + source
 */
function addUIDToSitesData(data: ISiteMngData[]) {
  const newData = data.map((item) => {
    const uid = `${item.smaRowId}_${item.source}`;
    return { ...item, uid };
  });

  newData.forEach((item) => {
    if (item.subRows && Array.isArray(item.subRows) && item.subRows.length > 0) {
      item.subRows.forEach((subItem) => {
        const subUID = `${subItem.smaRowId}_${subItem.source}`;
        subItem.uid = subUID;
      });
    }
  });

  return newData;
}

/**
 * Sorts the fetched sites data based on the orderMap so that staging is always showed on the top
 */
function customSort(arr: ISiteMngData[]) {
  const orderMap: Record<string, number> = {
    staging: 0,
    source: 1,
    published: 2,
  };

  arr.sort((a, b) => {
    const sourceA = a.source;
    const sourceB = b.source;

    const orderA = orderMap[sourceA] !== undefined ? orderMap[sourceA] : Infinity;
    const orderB = orderMap[sourceB] !== undefined ? orderMap[sourceB] : Infinity;

    return orderA - orderB;
  });

  return arr;
}

export const getSites = async (
  protocolName: string,
  language: string,
  siteDetailTarget: string
): Promise<any[]> => {
  const sitesFromDB = await fetchSites(protocolName, language, siteDetailTarget);
  const sitesWithUid = addUIDToSitesData(sitesFromDB);
  const sitessorted = customSort(sitesWithUid);
  // sitessorted.forEach((item) => {
  //   item.approve = false;
  // });
  return sitessorted;
};

export async function verifyJwtToken(jwtToken: string): Promise<any> {
  try {
    const apiUrl = '/api/verify-sma-token';
    const response: AxiosResponse<any> = await axios.post(apiUrl, {
      token: jwtToken,
    });
    return response.data;
  } catch (error) {
    return { isValid: false, payload: {} };
  }
}

export const saveCTMSSitesToRawDb = async (
  protocolName: string,
  globalSettings: any = {},
  statusList: string, // Comma separated value of all site status options
  siteDetailTarget: string,
  requestedBy: string
) => {
  const {
    optedIn = false,
    radius = 0,
    //default priority is blank
    priority = '',
    threshold = 0,
    warmTransferNumber = '',
    optInDateTime = new Date().toISOString(),
    isDefaultVirtualSite = false,
    isHybridSite = false,
    kiElementsEnabled = false,
    siteDirectedSchedule = false,
    language = 'en',
  } = globalSettings;
  const reqBody = {
    protocols: protocolName,
    siteStatuses: statusList,
    updatedBy: 'CTMS',
    siteDetailTarget,
    requestedBy,
    config: {
      isDefaultVirtualSite,
      isHybridSite,
      kiElementsEnabled,
      optedIn,
      optInDateTime,
      priority,
      radius,
      siteDirectedSchedule,
      threshold,
      warmTransferNumber,
      language,
    },
  };

  try {
    await post('/CTMS/SaveCTMSSitesToRawDb', reqBody);
  } catch (error) {
    throw error;
  }
};

export const getCountryStatesList = async (lang: string): Promise<IProtocolListRes> => {
  try {
    const countryItemId = '{0BAEDE80-BEE8-493D-B548-B60A6935A31C}';
    const response = await axiosSiteApi.get(
      `/SMA/GetGlobalStateData?globalCountryItemId=${countryItemId}&language=${lang}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Grid bookkeeping. changedFields skips these so they are not stored as edits.
const AUDIT_SKIP_FIELDS = new Set(['uid', 'smaRowId', 'subRows', 'isSubRow', 'source']);
// rememberAudit keeps these event types when the user edits the new row again before Save.
const STICKY_AUDIT_EVENTS = new Set<IAuditEventType>(['Created', 'Cloned', 'Translated']);

// Treats null and '' as the same empty value. Used by changedFields and rememberAudit, not by the popup.
export function normalizeAuditValue(value: unknown): unknown {
  if (value == null || value === '') return '';
  return value;
}

// Stored in ChangesJson when a field is cleared. rememberAudit treats this dash as blank.
export const EMPTY_AUDIT_VALUE = '—';

// Picks the Sitecore field list the grid already uses. Called from SitesTable and FooterActions.
export function auditFieldSettings<T>(
  epr: string | undefined,
  fieldOptions: T[] | null | undefined,
  eprFieldOptions: T[] | null | undefined
): T[] | null | undefined {
  return epr === EPR_TYPE.Global && (fieldOptions?.length ?? 0) > 0
    ? fieldOptions
    : eprFieldOptions;
}

// Builds grid labels and state-name lookup. Called from SitesTable, FooterActions, and translate.
export function auditCompareOptions(
  fieldSettings: { fieldKey?: string; fieldLabel?: string }[] | null | undefined,
  stateOptions: { value?: string; name?: string }[] | null | undefined
): { labels: Record<string, string>; stateNamesById: Record<string, string> } {
  const labels: Record<string, string> = {};
  (fieldSettings || []).forEach((item) => {
    if (item?.fieldKey && item.fieldLabel) labels[item.fieldKey] = item.fieldLabel;
  });
  const stateNamesById: Record<string, string> = {};
  (stateOptions || []).forEach((item) => {
    if (item?.value && item.name) stateNamesById[item.value] = item.name;
  });
  return { labels, stateNamesById };
}

// GUID list to state names for virtualSiteParticipatingStates. Used only by auditStoredValue.
function participatingStateNames(value: unknown, stateNamesById: Record<string, string>): unknown {
  if (typeof value !== 'string' || !value) return value;
  const names = value
    .split('|')
    .filter((id) => id)
    .map((id) => stateNamesById[id] || id);
  return names.join(',');
}

// Prepares the New value stored by changedFields. State GUIDs become names. Blank becomes a dash.
function auditStoredValue(
  key: string,
  value: unknown,
  stateNamesById: Record<string, string>
): unknown {
  const display =
    key === 'virtualSiteParticipatingStates'
      ? participatingStateNames(value, stateNamesById)
      : value;
  if (normalizeAuditValue(display) === '') return EMPTY_AUDIT_VALUE;
  return display;
}

// Diff of two rows. SitesTable and FooterActions call this before rememberAudit. Field is the grid label. key is internal.
export function changedFields(
  beforeRow: Record<string, unknown>,
  afterRow: Record<string, unknown>,
  options?: { labels?: Record<string, string>; stateNamesById?: Record<string, string> }
): IAuditChange[] {
  const labels = options?.labels || {};
  const stateNamesById = options?.stateNamesById || {};
  const keys = new Set([...Object.keys(beforeRow || {}), ...Object.keys(afterRow || {})]);
  const changes: IAuditChange[] = [];
  keys.forEach((key) => {
    if (AUDIT_SKIP_FIELDS.has(key)) return;
    const beforeValue = normalizeAuditValue(beforeRow[key]);
    const afterValue = normalizeAuditValue(afterRow[key]);
    if (beforeValue !== afterValue) {
      changes.push({
        key,
        Field: labels[key] || key,
        New: auditStoredValue(key, afterRow[key], stateNamesById),
      });
    }
  });
  return changes;
}

// Merges the pending draft in auditBySiteId. Called from SitesTable (add, clone, edit, opt-in) and FooterActions (translate).
// Created, Cloned, and Translated keep the first baseline and merge each edit. A later edit of those rows is not a full diff against that baseline.
// Updated replaces the change list. Each row save already diffs the whole row against cloneData, so a field left out was reverted and must not stay.
export function rememberAudit(
  map: Record<string, IAuditEntry>,
  incoming: {
    siteId: string;
    language: string;
    siteDetailTarget: string;
    protocolName: string;
    eventType: IAuditEventType;
    changes: IAuditChange[];
    source: IAuditSource | null;
    baseline: Record<string, unknown> | null;
  }
): Record<string, IAuditEntry> {
  const next = { ...map };
  const existing = next[incoming.siteId];
  const eventType =
    existing && STICKY_AUDIT_EVENTS.has(existing.eventType)
      ? existing.eventType
      : incoming.eventType;
  const sticky = existing != null && STICKY_AUDIT_EVENTS.has(existing.eventType);
  const baseline = sticky
    ? (existing?.baseline ?? incoming.baseline ?? null)
    : (incoming.baseline ?? existing?.baseline ?? null);
  const merged = new Map<string, IAuditChange>();
  if (sticky) {
    existing?.changes.forEach((change) => merged.set(change.key || change.Field, change));
  }
  incoming.changes.forEach((change) => merged.set(change.key || change.Field, change));
  if (baseline) {
    Array.from(merged.keys()).forEach((fieldKey) => {
      const change = merged.get(fieldKey);
      if (!change) return;
      const stored = change.New === EMPTY_AUDIT_VALUE ? '' : change.New;
      if (normalizeAuditValue(baseline[fieldKey]) === normalizeAuditValue(stored)) {
        merged.delete(fieldKey);
      }
    });
  }
  const changes = Array.from(merged.values());
  if (eventType === 'Updated' && changes.length === 0) {
    delete next[incoming.siteId];
    return next;
  }
  next[incoming.siteId] = {
    siteId: incoming.siteId,
    language: incoming.language || existing?.language || '',
    siteDetailTarget: incoming.siteDetailTarget || existing?.siteDetailTarget || '',
    protocolName: incoming.protocolName || existing?.protocolName || '',
    eventType,
    changes,
    source: existing?.source ?? incoming.source,
    baseline,
  };
  return next;
}

// Turns a draft into the POST body. FooterActions recordAudit calls this. Strips key. Source keys become grid labels.
export function toAuditRequestRow(input: {
  entry: Pick<
    IAuditEntry,
    'siteId' | 'language' | 'siteDetailTarget' | 'protocolName' | 'eventType' | 'changes' | 'source'
  >;
  transactionId: string;
  updatedBy: string;
  status: IAuditStatus;
  exception: string | null;
  labels?: Record<string, string>;
}): IAddSiteAuditTrailRow {
  return {
    SiteID: input.entry.siteId,
    Language: input.entry.language,
    SiteDetailTarget: input.entry.siteDetailTarget,
    ProtocolName: input.entry.protocolName,
    EventType: input.entry.eventType,
    TransactionId: input.transactionId,
    UpdatedBy: input.updatedBy,
    ChangesJson: JSON.stringify(
      (input.entry.changes ?? []).map(({ Field, New }) => ({ Field, New }))
    ),
    SourceJson: input.entry.source
      ? JSON.stringify(auditSourceRecord(input.entry.source, input.labels))
      : null,
    Status: input.status,
    Exception: input.exception,
  };
}
// POST /SMA/AddSiteAuditTrails. FooterActions and Filters call this. True only for HTTP 200 or 201.
export const addSiteAuditTrails = async (rows: IAddSiteAuditTrailRow[]): Promise<boolean> => {
  try {
    const response = await post('/SMA/AddSiteAuditTrails', { rows });
    return response.status === 200 || response.status === 201;
  } catch (error) {
    logger.error('AddSiteAuditTrails failed', { error });
    return false;
  }
};

// SMA_AUDIT_MONTHS to a FromDate, or null for full history. Used only by fetchSiteAuditTrails.
export function auditTrailFromDate(now: Date = new Date()): string | null {
  const raw = process.env.SMA_AUDIT_MONTHS;
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const months = parseInt(raw, 10);
  if (!Number.isFinite(months) || months <= 0) {
    return null;
  }
  const from = new Date(now.getTime());
  from.setUTCMonth(from.getUTCMonth() - months);
  return from.toISOString();
}

// True when one ChangesJson item has Field. Used by parseAuditChanges.
function isFieldChange(value: unknown): value is { Field: unknown; New?: unknown } {
  return value != null && typeof value === 'object' && !Array.isArray(value) && 'Field' in value;
}

// Parses ChangesJson or SourceJson once. Used by parseAuditChanges and parseAuditSourceRecord.
function readAuditJson(json: string | null): unknown {
  if (json == null || json.trim() === '') return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return json;
  }
}

// ChangesJson string to a field list, or the raw text when it is not that shape. Used by formatAuditUpdatedDetails.
function parseAuditChanges(
  changesJson: string | null
): { changes: { Field: unknown; New: unknown }[] } | { raw: string } {
  const parsed = readAuditJson(changesJson);
  if (parsed === undefined) return { changes: [] };
  if (typeof parsed === 'string' || !Array.isArray(parsed) || !parsed.every(isFieldChange)) {
    return { raw: changesJson ?? '' };
  }
  return {
    changes: parsed.map((item) => ({ Field: item.Field, New: item.New })),
  };
}

// The four source fields, their grid keys, and the label used when Sitecore has no label. Used when writing and reading source JSON.
const AUDIT_SOURCE_FIELDS = [
  { key: 'siteId', fieldKey: 'ctmsRowId', fallback: 'SiteID' },
  { key: 'protocolName', fieldKey: 'protocolName', fallback: 'Protocol' },
  { key: 'language', fieldKey: 'language', fallback: 'Language' },
  { key: 'siteDetailTarget', fieldKey: 'siteDetailTarget', fallback: 'SiteDetailTarget' },
] as const;

// Grid label for one source field. Used by auditSourceRecord and formatAuditSourceDetails.
function auditSourceLabel(
  fieldKey: string,
  fallback: string,
  labels?: Record<string, string>
): string {
  return labels?.[fieldKey] || fallback;
}

// Source object stored in SourceJson, keys are grid labels. Used by toAuditRequestRow.
function auditSourceRecord(
  source: IAuditSource,
  labels?: Record<string, string>
): Record<string, string> {
  const record: Record<string, string> = {};
  AUDIT_SOURCE_FIELDS.forEach(({ key, fieldKey, fallback }) => {
    record[auditSourceLabel(fieldKey, fallback, labels)] = source[key];
  });
  return record;
}

// SourceJson string back to an object. Used by formatAuditSourceDetails.
function parseAuditSourceRecord(sourceJson: string | null): Record<string, unknown> | null {
  const parsed = readAuditJson(sourceJson);
  if (
    !parsed ||
    typeof parsed === 'string' ||
    typeof parsed !== 'object' ||
    Array.isArray(parsed)
  ) {
    return null;
  }
  return parsed as Record<string, unknown>;
}

export interface IAuditDetailItem {
  label: string;
  value: string;
}

// Bullets for the Updated Details column. Called from AuditTrailModal. Cleared fields are already stored as a dash.
export function formatAuditUpdatedDetails(changesJson: string | null): IAuditDetailItem[] | string {
  const parsed = parseAuditChanges(changesJson);
  if ('raw' in parsed) return parsed.raw;
  return parsed.changes.map((change) => ({
    label: auditText(change.Field),
    value: auditText(change.New),
  }));
}

// Bullets or NA for the source column. Called from AuditTrailModal. Empty source is NA. Keys are the grid labels.
export function formatAuditSourceDetails(
  sourceJson: string | null,
  labels?: Record<string, string>
): IAuditDetailItem[] | 'NA' {
  const record = parseAuditSourceRecord(sourceJson);
  if (!record) return 'NA';
  const items = AUDIT_SOURCE_FIELDS.map(({ fieldKey, fallback }) => {
    const label = auditSourceLabel(fieldKey, fallback, labels);
    const value = record[label];
    if (value == null) return null;
    return { label, value: auditText(value) };
  }).filter((item): item is IAuditDetailItem => item != null);
  return items.length > 0 ? items : 'NA';
}

// First present property among PascalCase and camelCase names. Used by normalizeSiteAuditTrailRow.
function pickAuditField(record: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined) return record[key];
  }
  return undefined;
}

// Response value to a string. Used while normalizing a fetched row and while formatting bullets.
function auditText(value: unknown): string {
  if (value == null) return '';
  return typeof value === 'string' ? value : String(value);
}

// Same as auditText, but null stays null. Used by normalizeSiteAuditTrailRow for optional columns.
function auditTextOrNull(value: unknown): string | null {
  return value == null ? null : auditText(value);
}

// One Fetch response object to ISiteAuditTrailRow. Used only by fetchSiteAuditTrails. Accepts either JSON casing.
function normalizeSiteAuditTrailRow(row: unknown): ISiteAuditTrailRow {
  const record =
    row && typeof row === 'object'
      ? (row as Record<string, unknown>)
      : ({} as Record<string, unknown>);
  const id = pickAuditField(record, 'Id', 'id');
  return {
    Id: typeof id === 'number' || typeof id === 'string' ? id : '',
    SiteID: auditText(pickAuditField(record, 'SiteID', 'siteID')),
    Language: auditText(pickAuditField(record, 'Language', 'language')),
    SiteDetailTarget: auditText(pickAuditField(record, 'SiteDetailTarget', 'siteDetailTarget')),
    ProtocolName: auditText(pickAuditField(record, 'ProtocolName', 'protocolName')),
    EventType: auditText(pickAuditField(record, 'EventType', 'eventType')),
    Status: auditTextOrNull(pickAuditField(record, 'Status', 'status')),
    UpdatedBy: auditText(pickAuditField(record, 'UpdatedBy', 'updatedBy')),
    ChangesJson: auditTextOrNull(pickAuditField(record, 'ChangesJson', 'changesJson')),
    SourceJson: auditTextOrNull(pickAuditField(record, 'SourceJson', 'sourceJson')),
    InsertedDateTime: auditText(pickAuditField(record, 'InsertedDateTime', 'insertedDateTime')),
    Exception: auditTextOrNull(pickAuditField(record, 'Exception', 'exception')),
    TransactionId: auditText(pickAuditField(record, 'TransactionId', 'transactionId')),
  };
}

// POST /SMA/FetchSiteAuditTrails. SitesTable openAuditTrail calls this. Null means the request failed.
export const fetchSiteAuditTrails = async (request: {
  SiteID: string;
  Language: string;
  SiteDetailTarget: string;
}): Promise<ISiteAuditTrailRow[] | null> => {
  const body: IFetchSiteAuditTrailsRequest = {
    SiteID: request.SiteID,
    Language: request.Language,
    SiteDetailTarget: request.SiteDetailTarget,
    FromDate: auditTrailFromDate(),
  };
  try {
    const response = await post('/SMA/FetchSiteAuditTrails', body);
    if (response.status === 200 && Array.isArray(response.data)) {
      return response.data.map(normalizeSiteAuditTrailRow);
    }
    logger.error('FetchSiteAuditTrails failed', { status: response?.status });
    return null;
  } catch (error) {
    logger.error('FetchSiteAuditTrails failed', { error });
    return null;
  }
};
